import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit, SkipSelf } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService, ToastService, CachedValuesService } from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs';
import { CounterpartResponse } from '@services/api-client/responses';
import { Counterpart } from 'app/types';
import { CounterpartForm } from './create-and-update-counterpart.model';
import { AgreementPartsComponent } from '../parts/agreement-parts.component';

@UntilDestroy()
@Component({
    selector: 'app-create-and-update-counterpart',
    templateUrl: './create-and-update-counterpart.component.html',
    styleUrl: './create-and-update-counterpart.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateAndUpdateCounterpartComponent implements OnInit {
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    public showPotentialValidationMessages = false;
    public loading: boolean;
    public counterpartForm: FormGroup<CounterpartForm>;
    //@Input() public counterpart?: Counterpart;

    private _counterpart?: Counterpart;
    @Input()
    public set counterpart(value: Counterpart) {
        this._counterpart = value;
        // Only reload form if counterpart actually changed (not just initial null)
        if (value || !this.counterpartForm) {
            this.loadForm();
        }
    }

    public get counterpart(): Counterpart {
        return this._counterpart;
    }

    @Input()
    public set initialCompanyName(value: string | undefined) {
        this._initialCompanyName = value;
        if (this.counterpartForm) {
            // If form already exists (setter called after form creation), update it
            this.counterpartForm.get('companyName')?.setValue(value || '');
            this.counterpartForm.markAsDirty();
            this.changeDetectorRef.markForCheck();
            this.changeDetectorRef.detectChanges();
        }
        // If form doesn't exist yet, it will be created with this value in loadForm()
    }

    private _initialCompanyName?: string;
    public get initialCompanyName(): string | undefined {
        return this._initialCompanyName;
    }

    @SkipSelf() private readonly partsComponent = inject(AgreementPartsComponent);

    //private readonly createdSubject: BehaviorSubject<Counterpart> = new BehaviorSubject<Counterpart>(null);
    //public created$: Observable<Counterpart> = this.createdSubject.asObservable();

    public get f() {
        return this.counterpartForm.controls;
    }

    public ngOnInit() {
        this.loadForm();
    }

    private loadForm() {
        const fb = new FormBuilder();
        this.counterpartForm = fb.nonNullable.group({
            companyName: [this.counterpart?.companyName || this._initialCompanyName || '', [Validators.required]],
            organizationNo: [this.counterpart?.organizationNo || ''],
            address: [this.counterpart?.address || ''],
            zipCode: [this.counterpart?.zipCode || ''],
            city: [this.counterpart?.city || ''],
            country: [this.counterpart?.country || ''],
            fullname: [this.counterpart?.fullname || ''],
            email: [this.counterpart?.email || ''],
            title: [this.counterpart?.title || ''],
            phone: [this.counterpart?.phone || ''],
            linkedin: [this.counterpart?.linkedin || ''],
            website: [this.counterpart?.website || '']
        });

        if (this.counterpart) {
            this.counterpartForm.disable();
        } else if (this._initialCompanyName) {
            // If form is being created with a pre-filled company name from metadata,
            // mark it as dirty so the save button is enabled
            this.counterpartForm.markAsDirty();
            this.changeDetectorRef.markForCheck();
            this.changeDetectorRef.detectChanges();
        }
    }

    public isDisabled() {
        return this.loading || !this.counterpartForm.dirty;
    }

    public save() {
        if (this.counterpartForm.dirty) {
            this.showPotentialValidationMessages = true;

            if (this.counterpartForm.valid) {
                this.apiClientService
                    .createCounterpart$({
                        companyName: this.f.companyName.value,
                        organizationNo: this.f.organizationNo.value,
                        address: this.f.address.value,
                        zipCode: this.f.zipCode.value,
                        city: this.f.city.value,
                        country: this.f.country.value,
                        fullname: this.f.fullname.value,
                        title: this.f.title.value,
                        email: this.f.email.value,
                        phone: this.f.phone.value,
                        linkedin: this.f.linkedin.value,
                        website: this.f.website.value
                    })
                    .pipe(untilDestroyed(this), take(1))
                    .subscribe({
                        next: (response: CounterpartResponse) => {
                            // Invalidate the counterparts cache so the newly created counterpart appears in the dropdown
                            this.cachedValuesService.invalidateCache();
                            // Hide the form after successful creation
                            this.partsComponent.showPartsInfo = false;
                            // Reset form state
                            this.showPotentialValidationMessages = false;
                            this.counterpartForm.reset();
                            const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                            const message = this.translateService.instant(
                                'TOAST_MESSAGES.CREATE_COUNTERPART_SUCCESS'
                            ) as string;
                            this.partsComponent.selectCounterpart(response.id);
                            this.toastService.open(title, message, { type: 'success' });
                        },
                        error: () => {
                            const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                            const message = this.translateService.instant(
                                'TOAST_MESSAGES.CREATE_COUNTERPART_ERROR'
                            ) as string;
                            this.toastService.open(title, message, { type: 'error', progressBar: true });
                        }
                    });
            }
        }
    }
}
